import React, { useState } from "react";
import { Button, Descriptions, message, Modal, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  GET_DSP_ADJUST_BILL_DETAILS,
  GetDspAdjustBillDetailsData,
  GetDspAdjustBillDetailsVariables,
  DspAdjustBillDetailItem,
} from "../graphql/queries/getDspAdjustBillDetails";
import { DspAdjustBillTask } from "../graphql/queries/getDspAdjustBillTask";
import {
  GET_DSP_ADJUST_BILL_CSV_DOWNLOAD_URL,
  GetDspAdjustBillCsvDownloadUrlData,
  GetDspAdjustBillCsvDownloadUrlVariables,
} from "../graphql/queries/getDspAdjustBillCsvDownloadUrl";
import {
  GET_DSP_ADJUST_BILL_DETAILS_CSV_URL,
  GetDspAdjustBillDetailsCsvUrlData,
  GetDspAdjustBillDetailsCsvUrlVariables,
} from "../graphql/queries/getDspAdjustBillDetailsCsvUrl";

interface Props {
  task: DspAdjustBillTask;
  visible: boolean;
  onClose: () => void;
}

const STATUS_TAG: Record<number, React.ReactNode> = {
  0: <Tag color="blue">处理中</Tag>,
  1: <Tag color="blue">处理中</Tag>,
  2: <Tag color="green">已完成</Tag>,
  3: <Tag color="red">失败</Tag>,
  4: <Tag color="red">失败</Tag>,
};

/** 美分 → 美元，保留2位小数 */
const centsToDollar = (v: number | null | undefined): string => {
  if (v == null) return "-";
  return (v / 100).toFixed(2);
};

export const DspAdjustBillTaskModal: React.FC<Props> = ({
  task,
  visible,
  onClose,
}) => {
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const [fetchDownloadUrl, { loading: downloadLoading }] = useLazyQuery<
    GetDspAdjustBillCsvDownloadUrlData,
    GetDspAdjustBillCsvDownloadUrlVariables
  >(GET_DSP_ADJUST_BILL_CSV_DOWNLOAD_URL, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      window.open(data.getDspAdjustBillCsvDownloadUrl, "_blank");
    },
    onError: (err) => {
      message.error("获取下载链接失败: " + err.message);
    },
  });

  const [fetchDetailsCsvUrl, { loading: detailsCsvLoading }] = useLazyQuery<
    GetDspAdjustBillDetailsCsvUrlData,
    GetDspAdjustBillDetailsCsvUrlVariables
  >(GET_DSP_ADJUST_BILL_DETAILS_CSV_URL, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      window.open(data.getDspAdjustBillDetailsCsvUrl, "_blank");
    },
    onError: (err) => {
      message.error("导出明细失败: " + err.message);
    },
  });

  const { data, loading } = useQuery<
    GetDspAdjustBillDetailsData,
    GetDspAdjustBillDetailsVariables
  >(GET_DSP_ADJUST_BILL_DETAILS, {
    variables: { taskId: task.id, page, pageSize: PAGE_SIZE },
    skip: !visible,
    fetchPolicy: "no-cache",
  });

  const columns: ColumnsType<DspAdjustBillDetailItem> = [
    {
      title: "业务单号",
      dataIndex: "businessId",
      key: "businessId",
      width: 200,
    },
    {
      title: "目标金额（$）",
      dataIndex: "adjustAmount",
      key: "adjustAmount",
      width: 130,
      render: (v: number) => centsToDollar(v),
    },
    {
      title: "调账前金额（$）",
      dataIndex: "beforeAdjustAmount",
      key: "beforeAdjustAmount",
      width: 140,
      render: (v: number | null) => centsToDollar(v),
    },
    {
      title: "差异金额（$）",
      dataIndex: "diffAmount",
      key: "diffAmount",
      width: 130,
      render: (v: number | null) => centsToDollar(v),
    },
    {
      title: "结果",
      key: "result",
      width: 100,
      render: (_: unknown, record: DspAdjustBillDetailItem) =>
        record.errorMsg ? (
          <Tooltip title={record.errorMsg}>
            <Tag color="red">失败</Tag>
          </Tooltip>
        ) : (
          <Tag color="green">成功</Tag>
        ),
    },
    {
      title: "失败原因",
      dataIndex: "errorMsg",
      key: "errorMsg",
      ellipsis: true,
    },
  ];

  return (
    <Modal
      title="调账任务详情"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={980}
    >
      <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="账单ID">
          {task.billId}
        </Descriptions.Item>
        <Descriptions.Item label="任务状态">
          {STATUS_TAG[task.status] ?? <Tag>未知</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="成功 / 失败">
          {task.successCount} / {task.failCount}
        </Descriptions.Item>
        <Descriptions.Item label="提交时间(America/Los_Angeles)">
          {task.createdAt ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label="完成时间(America/Los_Angeles)">
          {task.finishedTime ?? "-"}
        </Descriptions.Item>
        {task.status === 3 && task.errorMsg && (
          <Descriptions.Item label="失败原因" span={3}>
            <span style={{ color: "#ff4d4f" }}>{task.errorMsg}</span>
          </Descriptions.Item>
        )}
      </Descriptions>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, margin: "12px 0" }}>
        <Button
          loading={downloadLoading}
          onClick={() => fetchDownloadUrl({ variables: { taskId: task.id } })}
        >
          下载原始CSV
        </Button>
        <Button
          loading={detailsCsvLoading}
          onClick={() => fetchDetailsCsvUrl({ variables: { taskId: task.id } })}
        >
          导出调账明细
        </Button>
      </div>
      <Table<DspAdjustBillDetailItem>
        rowKey="id"
        loading={loading}
        dataSource={data?.getDspAdjustBillDetails.list ?? []}
        columns={columns}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.getDspAdjustBillDetails.total ?? 0,
          onChange: setPage,
          showTotal: (total) => `共 ${total} 条`,
          showSizeChanger: false,
        }}
        scroll={{ x: 800 }}
      />
    </Modal>
  );
};
